import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Spin, Tooltip, message } from 'antd';
import { C, VERDICT_STYLES } from './HCCReviewConstants';
import AgentConfigService from '../../services/AgentConfigService';

// ── AuditResultsPopup ─────────────────────────────────────────────────────────
export const AuditResultsPopup = ({ visible, onClose, findings, auditMeta, onRerun }) => {
  if (!visible) return null;
 
  const suspiciousCount = findings.filter(f => f.verdict === 'suspicious').length;
  const clearCount      = findings.filter(f => f.verdict === 'clear').length;
  const errorCount      = findings.filter(f => f.verdict === 'error').length;
 
  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      title={null}
      width={640}
      style={{ top: 60 }}
      bodyStyle={{ padding: 0, maxHeight: '70vh', overflowY: 'auto' }}
    >
      {/* Header */}
      <div style={{
        padding: '20px 24px 16px',
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: C.navy }}>
          Audit Results
        </div>
        <div style={{
          fontSize: 14, color: C.textMuted, marginTop: 4,
          display: 'flex', gap: 12, flexWrap: 'wrap',
        }}>
          <span>{findings.length} code{findings.length !== 1 ? 's' : ''} evaluated</span>
          {suspiciousCount > 0 && (
            <span style={{ color: '#DC2626', fontWeight: 600 }}>
              {suspiciousCount} suspicious
            </span>
          )}
          {clearCount > 0 && (
            <span style={{ color: '#166534', fontWeight: 600 }}>
              {clearCount} clear
            </span>
          )}
          {errorCount > 0 && (
            <span style={{ color: '#6B7280' }}>{errorCount} error</span>
          )}
          {auditMeta?.audited_by && (
            <span style={{ color: C.textMuted }}>
              by {auditMeta.audited_by}
            </span>
          )}
        </div>
      </div>
 
      {/* Findings list */}
      <div style={{ padding: '8px 0' }}>
        {findings.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: C.textMuted }}>
            No codes were evaluated
          </div>
        ) : (
          findings.map((f, idx) => {
            const vs = VERDICT_STYLES[f.verdict] || VERDICT_STYLES.error;
            return (
              <div
                key={idx}
                style={{
                  margin: '8px 16px',
                  padding: '14px 16px',
                  borderRadius: 8,
                  border: `1px solid ${vs.border}`,
                  background: vs.bg,
                }}
              >
                {/* Code + verdict badge */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: 8,
                }}>
                  <div>
                    <span style={{ fontSize: 16, fontWeight: 700, fontFamily: 'monospace' }}>
                      {f.suspect_code}
                    </span>
                    {f.companion_codes?.length > 0 && (
                      <span style={{ fontSize: 13, color: C.textMuted, marginLeft: 8 }}>
                        + {f.companion_codes.join(', ')}
                      </span>
                    )}
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                    padding: '3px 10px', borderRadius: 4,
                    color: vs.color,
                    border: `1px solid ${vs.border}`,
                    background: 'white',
                  }}>
                    {vs.label}
                  </span>
                </div>
 
                {/* Tags row */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                  {f.severity && (
                    <span style={{
                      fontSize: 11, padding: '2px 7px', borderRadius: 3,
                      background: f.severity === 'critical' ? '#FEE2E2' : '#FEF3C7',
                      color: f.severity === 'critical' ? '#991B1B' : '#92400E',
                      fontWeight: 600,
                    }}>
                      {f.severity}
                    </span>
                  )}
                  {f.condition_area && (
                    <span style={{
                      fontSize: 11, padding: '2px 7px', borderRadius: 3,
                      background: '#EFF6FF', color: '#1E40AF', fontWeight: 600,
                    }}>
                      {f.condition_area}
                    </span>
                  )}
                  {f.rule_ids?.map(rid => (
                    <span key={rid} style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 3,
                      background: '#F3F4F6', color: '#6B7280', fontFamily: 'monospace',
                    }}>
                      {rid}
                    </span>
                  ))}
                </div>
 
                {/* Verdict reason */}
                {f.verdict_reason && (
                  <div style={{
                    fontSize: 14, lineHeight: 1.55, color: '#374151',
                  }}>
                    {f.verdict_reason}
                  </div>
                )}
 
                {/* Rule summary */}
                <div style={{
                  fontSize: 12, color: C.textMuted, marginTop: 6,
                  fontStyle: 'italic',
                }}>
                  {f.summary}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer with Re-run button */}
      <div style={{
        padding: '12px 24px',
        borderTop: `1px solid ${C.border}`,
        display: 'flex', justifyContent: 'flex-end', gap: 8,
      }}>
        <button
          onClick={onRerun}
          style={{
            padding: '7px 18px', fontSize: 14, fontWeight: 600,
            borderRadius: 5, cursor: 'pointer',
            background: '#FFF7ED', color: '#9A3412',
            border: '1.5px solid #FB923C',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#FFEDD5'}
          onMouseLeave={e => e.currentTarget.style.background = '#FFF7ED'}
        >
          Re-run Audit
        </button>
        <button
          onClick={onClose}
          data-telemetry-label="close_audit_popup"
          style={{
            padding: '7px 18px', fontSize: 14, fontWeight: 600,
            borderRadius: 5, cursor: 'pointer',
            background: '#F9FAFB', color: '#374151',
            border: '1.5px solid #D1D5DB',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
          onMouseLeave={e => e.currentTarget.style.background = '#F9FAFB'}
        >
          Close
        </button>
      </div>


    </Modal>
  );
};


// ── useAudit hook ─────────────────────────────────────────────────────────────
export const useAudit = ({ encounterTrace, selectedEnc, chartId, sessionId }) => {

  const AUDIT_ENABLED = process.env.REACT_APP_AUDIT_ENABLED === 'true';

  // ── State ─────────────────────────────────────────────────────────────────
  const [auditAvailable,     setAuditAvailable]     = useState(false);
  const [auditSuspects,      setAuditSuspects]      = useState([]);
  const [auditPhase,         setAuditPhase]         = useState('idle');
  const [auditProgress,      setAuditProgress]      = useState({ total: 0, completed: [], pending: [], percentage: 0 });
  const [auditFindings,      setAuditFindings]      = useState([]);
  const [auditMeta,          setAuditMeta]          = useState(null);
  const [auditPopupVisible,  setAuditPopupVisible]  = useState(false);
  const [auditConfirmVisible,setAuditConfirmVisible]= useState(false);
  const auditPollRef = useRef(null);

  const getAuditBaseUrl = useCallback(
    () => AgentConfigService.getAuditServiceUrl(),
    []
  );

  // ── Polling ───────────────────────────────────────────────────────────────
  function _startPolling() {
    // Clear any existing poll
    if (auditPollRef.current) clearInterval(auditPollRef.current);
 
    const pollStarted = Date.now();
    const POLL_INTERVAL = 3000;   // 3 seconds
    const POLL_TIMEOUT  = 120000; // 2 minutes
 
    auditPollRef.current = setInterval(async () => {
      // Timeout guard
      if (Date.now() - pollStarted > POLL_TIMEOUT) {
        clearInterval(auditPollRef.current);
        auditPollRef.current = null;
        setAuditPhase('error');
        message.warning('Audit timed out — try again');
        return;
      }
 
      try {
        const auditBase = await getAuditBaseUrl();
        const encId = selectedEnc?.encounter_id;
        if (!encId) return;
 
        const resp = await fetch(
          `${auditBase}/audit/charts/${chartId}/encounters/${encId}/status`,
          {
            headers: { Authorization: `Bearer ${sessionId}` },
            credentials: 'include',
          }
        );
 
        if (!resp.ok) return;
        const data = await resp.json();
 
        setAuditProgress({
          total: data.total || 0,
          completed: data.completed || [],
          pending: data.pending || [],
          percentage: data.percentage || 0,
        });
        setAuditFindings(data.findings || []);
 
        if (data.status === 'completed') {
          clearInterval(auditPollRef.current);
          auditPollRef.current = null;
          setAuditPhase('completed');
          setAuditMeta({
            audited_by: data.started_by,
            audited_at: data.completed_at,
          });
          // Auto-open results popup
          setAuditPopupVisible(true);
 
          const suspicious = (data.findings || []).filter(f => f.verdict === 'suspicious').length;
          if (suspicious > 0) {
            message.warning(`Audit flagged ${suspicious} code${suspicious !== 1 ? 's' : ''} as suspicious`);
          } else {
            message.success('Audit complete — no suspicious codes found');
          }
        } else if (data.status === 'error') {
          clearInterval(auditPollRef.current);
          auditPollRef.current = null;
          setAuditPhase('error');
          message.error('Audit failed — ' + (data.error || 'unknown error'));
        }
      } catch (err) {
        console.error('Audit poll error:', err);
      }
    }, POLL_INTERVAL);
  }

  // ── Check rules + existing status on encounter load ───────────────────────
  useEffect(() => {
    // Reset audit state on encounter change
    setAuditAvailable(false);
    setAuditSuspects([]);
    setAuditPhase('idle');
    setAuditProgress({ total: 0, completed: [], pending: [], percentage: 0 });
    setAuditFindings([]);
    setAuditMeta(null);
    setAuditPopupVisible(false);
 
    // Clear any existing poll
    if (auditPollRef.current) {
      clearInterval(auditPollRef.current);
      auditPollRef.current = null;
    }
 
    if (!AUDIT_ENABLED) return;
 
    const codes = (encounterTrace?.coded_findings || [])
      .map(f => f.icd10_code)
      .filter(Boolean);
 
    if (codes.length === 0) return;
 
    let cancelled = false;
 
    (async () => {
      try {
        const auditBase = await getAuditBaseUrl();
 
        // 1. Check if audit rules exist for these codes
        const checkResp = await fetch(`${auditBase}/audit/check-rules`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionId}`,
          },
          credentials: 'include',
          body: JSON.stringify({ codes }),
        });
 
        if (checkResp.ok && !cancelled) {
          const checkData = await checkResp.json();
          setAuditAvailable(checkData.audit_available || false);
          setAuditSuspects(checkData.suspect_codes || []);
        }
 
        // 2. Check if an audit has already been run (or is running)
        if (selectedEnc && !cancelled) {
          const statusResp = await fetch(
            `${auditBase}/audit/charts/${chartId}/encounters/${selectedEnc.encounter_id}/status`,
            {
              headers: { Authorization: `Bearer ${sessionId}` },
              credentials: 'include',
            }
          );
 
          if (statusResp.ok && !cancelled) {
            const statusData = await statusResp.json();
 
            if (statusData.status === 'completed') {
              setAuditPhase('completed');
              setAuditFindings(statusData.findings || []);
              setAuditProgress({
                total: statusData.total,
                completed: statusData.completed,
                pending: [],
                percentage: 100,
              });
              setAuditMeta({
                audited_by: statusData.started_by,
                audited_at: statusData.completed_at,
              });
            } else if (statusData.status === 'running') {
              // Resume polling
              setAuditPhase('running');
              setAuditProgress({
                total: statusData.total,
                completed: statusData.completed || [],
                pending: statusData.pending || [],
                percentage: statusData.percentage || 0,
              });
              setAuditFindings(statusData.findings || []);
              _startPolling();
            }
          }
        }
      } catch (err) {
        console.error('Audit init error:', err);
      }
    })();
 
    return () => {
      cancelled = true;
      if (auditPollRef.current) {
        clearInterval(auditPollRef.current);
        auditPollRef.current = null;
      }
    };
  }, [encounterTrace, sessionId, chartId, selectedEnc?.encounter_id]);
  // Note: _startPolling is defined below — hoisted via function declaration

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleAuditClick = useCallback(() => {
    if (auditPhase === 'completed') {
      // Re-open results popup
      setAuditPopupVisible(true);
    } else if (auditPhase === 'idle') {
      // Show confirmation
      setAuditConfirmVisible(true);
    }
    // If running, button is disabled — no action
  }, [auditPhase]);

  const handleAuditConfirm = useCallback(async () => {
    setAuditConfirmVisible(false);
 
    if (!selectedEnc) return;
 
    try {
      const auditBase = await getAuditBaseUrl();
      const resp = await fetch(
        `${auditBase}/audit/charts/${chartId}/encounters/${selectedEnc.encounter_id}/run`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionId}`,
          },
          credentials: 'include',
        }
      );
 
      if (!resp.ok) throw new Error(`${resp.status}`);
      const data = await resp.json();
 
      if (data.status === 'started' || data.status === 'already_running') {
        setAuditPhase('running');
        setAuditProgress(prev => ({ ...prev, total: data.total }));
        _startPolling();
      } else if (data.status === 'no_rules') {
        message.info('No audit rules matched for this encounter');
      }
    } catch (err) {
      console.error('Audit start error:', err);
      message.error('Failed to start audit');
    }
  }, [selectedEnc, chartId, sessionId, getAuditBaseUrl]);

  const handleAuditRerun = useCallback(() => {
    setAuditPopupVisible(false);
    setAuditPhase('idle');
    setAuditFindings([]);
    setAuditProgress({ total: 0, completed: [], pending: [], percentage: 0 });
    // Fire the run directly — skip confirmation since re-run is intentional
    handleAuditConfirm();
  }, [handleAuditConfirm]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (auditPollRef.current) {
        clearInterval(auditPollRef.current);
        auditPollRef.current = null;
      }
    };
  }, []);

  return {
    auditAvailable,
    auditSuspects,
    auditPhase,
    auditProgress,
    auditFindings,
    auditMeta,
    auditPopupVisible,
    auditConfirmVisible,
    setAuditPopupVisible,
    setAuditConfirmVisible,
    handleAuditClick,
    handleAuditConfirm,
    handleAuditRerun,
  };
};